import * as React from 'react'
import { useMutation } from '@apollo/react-hooks'

import {
  i18n,
  RateLimitBucket,
  Routes,
  useCaptcha,
  useLogger,
  useRequestConfig,
  useSiteConfig,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import { truncate } from '@thg-commerce/enterprise-core/src/utils/string'
import {
  EnhancedTrackingEvent,
  pushToEnhancedDataLayer,
} from '@thg-commerce/enterprise-metrics/src/data_layer'
import {
  AddReviewInput,
  AddReviewResponse,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { SafeHtml, Separator } from '@thg-commerce/gravity-elements'
import { DynamicForm } from '@thg-commerce/gravity-patterns'

import { PlatformActionType } from '../../../pages/product/[sku]'
import { AddReviewFormResult } from '../../../pages/product/addReview'
import { AddReview as ADD_REVIEW_MUTATION } from '../../graphql/AddReview.graphql'
import { AddReviewFormFields } from '../AddReviewForm'
import { ReviewInstructions } from '../ReviewInstructions'

import {
  BottomSeparatorGridItem,
  ErrorText,
  ImageGridItem,
  SeparatorGridItem,
  StyledButton,
  StyledGrid,
  StyledGridItem,
  StyledImage,
  StyledSeparator,
  Subtitle,
  Title,
} from './styles'

interface FieldValue {
  [key: string]: string
}

interface AddReviewFormTrackingEvent extends EnhancedTrackingEvent {
  product_id: number | string
  product_name?: string
  rating: string
}

type AddReviewFormProps = {
  sku: number
  externalIdentifier?: string
  reviewFormData?: AddReviewFormResult['addReviewForm']
}

export const AddReviewForm = (props: AddReviewFormProps) => {
  const logger = useLogger()
  const { originUrl } = useSiteDefinition()
  const { csrf } = useRequestConfig()
  const { useExternalIdentifier } = useSiteConfig()

  const i18nText = {
    title: i18n('review.addreview.title.text'),
    submitButtonLabel: i18n('review.addreview.submit.label'),
    failedToFetch: i18n('review.addreview.failed.text'),
  }

  const i18nFormText = {
    reviewRating: i18n('product.review.sort.rating'),
    reviewNickname: i18n('review.addreview.nickname.text'),
    reviewTitle: i18n('review.addreview.reviewtitle.text'),
    reviewYourReview: i18n('review.addreview.yourreview.text'),
    reviewGender: i18n('review.addreview.gender.text'),
    reviewGenderPlaceholder: i18n('review.addreview.gender.placeholder'),
    reviewAgeRange: i18n('review.addreview.age.text'),
    reviewAgeRangePlaceholder: i18n('review.addreview.age.placeholder'),
    reviewTaste: i18n('review.addreview.taste.text'),
    reviewMixability: i18n('review.addreview.mixability.text'),
    reviewEffectiveness: i18n('review.addreview.effectiveness.text'),
    reviewGoesWith: i18n('review.addreview.goeswellwith.text'),
    reviewQualityRating: i18n('order.review.featurerating.quality'),
    reviewValueRating: i18n('order.review.featurerating.value'),
    reviewFitRating: i18n('order.review.featurerating.fit'),
  }

  const addReviewForm = props.reviewFormData
  const masterSku = addReviewForm?.sku ?? 0
  const productPageUrl = `${originUrl}${Routes.Product.replace(
    ':sku',
    masterSku.toString(),
  )}.html`

  const [addReview] = useMutation<AddReviewResponse, { input: AddReviewInput }>(
    ADD_REVIEW_MUTATION,
    {
      onCompleted: (data) => {
        if (data.error || !submittedScore.current) {
          return
        }

        pushToEnhancedDataLayer<AddReviewFormTrackingEvent>({
          event: 'review_create',
          product_id:
            useExternalIdentifier && props.externalIdentifier
              ? props.externalIdentifier
              : props.sku.toString(),
          product_name: addReviewForm?.title,
          rating: submittedScore.current,
        })

        window.location.assign(
          `${productPageUrl}?reviewActionType=${PlatformActionType.reviewSuccessfulSubmission}`,
        )
      },
      onError: (error) => {
        logger.error(
          `[AddReview]: Failed to add review with error ${error.message}`,
        )
      },
    },
  )

  const captchaContainerRef = React.useRef<HTMLDivElement | null>(null)
  const captcha = useCaptcha({
    bucket: RateLimitBucket.ADD_REVIEW,
    containerRef: captchaContainerRef,
  })
  const submittedScore = React.useRef<string | undefined>()

  const onSubmitHandler = (fieldValue: FieldValue) => {
    captcha.submit((response) => {
      const fields = Object.entries(fieldValue)
        .filter(([, value]) => value)
        .map(([name, value]) => ({
          name,
          value,
          isProductSpecific:
            addReviewForm?.reviewForm.fields?.find(
              (field) => field.name.toLowerCase() === name.toLowerCase(),
            )?.type === 'FEATURERATING',
        }))

      submittedScore.current = fieldValue.score

      addReview({
        variables: {
          input: {
            fields,
            sku: masterSku,
          },
        },
        context: {
          headers: {
            'X-Captcha-Type': captcha.type,
            'X-Captcha-Response': response,
          },
        },
      })
    })
  }

  const isFormFieldsValid = addReviewForm?.reviewForm.fields?.every(
    (field) =>
      field.type === 'LIST' ||
      field.type === 'TEXT' ||
      field.type === 'NUMBER' ||
      field.type === 'FEATURERATING',
  )

  if (
    !addReviewForm ||
    !addReviewForm?.reviewForm.fields ||
    masterSku === 0 ||
    !isFormFieldsValid
  ) {
    return <ErrorText>{i18nText.failedToFetch}</ErrorText>
  }

  return (
    <StyledGrid>
      <StyledGridItem rowStart={1} colStart={1} colSpan={12}>
        <Title>{i18nText.title}</Title>
      </StyledGridItem>

      <ImageGridItem rowStart={2} colStart={1} colSpan={[12, 3, 2, 2]}>
        <StyledImage
          src={[
            {
              url: addReviewForm.image ?? '',
            },
          ]}
          alt={addReviewForm.title}
          width="100%"
          height="100%"
          isAmp={false}
          onClick={() => {
            window.location.assign(productPageUrl)
          }}
        />
      </ImageGridItem>

      <StyledGridItem
        rowStart={[3, 2, 2, 2]}
        colStart={[1, 5, 3, 3]}
        colSpan={[12, 8, 7, 5]}
      >
        <Subtitle>{addReviewForm.title}</Subtitle>
        <SafeHtml content={truncate(addReviewForm.synopsis, 20)} />
      </StyledGridItem>

      <SeparatorGridItem rowStart={[4, 3, 3, 3]} colStart={1} colSpan={12}>
        <Separator />
      </SeparatorGridItem>

      <StyledGridItem
        rowStart={[5, 5, 4, 4]}
        colStart={1}
        colSpan={[12, 12, 4, 4]}
      >
        <ReviewInstructions />
        <StyledSeparator />
      </StyledGridItem>

      <StyledGridItem
        rowStart={[6, 6, 4, 4]}
        colStart={[1, 1, 5, 5]}
        colSpan={[12, 9, 7, 5]}
      >
        <div ref={captchaContainerRef}>
          <DynamicForm
            csrf={csrf}
            onSubmit={onSubmitHandler}
            data-testid="dynamic-form-add-review"
            fields={AddReviewFormFields(
              addReviewForm?.reviewForm.fields,
              i18nFormText,
            )}
          >
            {captcha.captcha}
            <StyledButton
              type="submit"
              emphasis="high"
              data-testid="button-submit-add-review"
            >
              {i18nText.submitButtonLabel}
            </StyledButton>
          </DynamicForm>
        </div>
      </StyledGridItem>
      <BottomSeparatorGridItem
        rowStart={[7, 7, 5, 5]}
        colStart={1}
        colSpan={12}
      >
        <Separator />
      </BottomSeparatorGridItem>
    </StyledGrid>
  )
}
